import React, { useState, useEffect, useCallback, useMemo } from "react";
import Header from "../../commons/Header/Header";
import Sidebar from "../../commons/Sidebar/Sidebar";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Img from "../../assets/images/img.webp";
import { handleApiCall } from "../../apis";
import { endpoints } from "../../utils/endpoints";
import { toast } from "react-toastify";
import { UI_CONSTANTS } from "../../utils/constants";
import {
  LIST_RETURN_KEYS,
  persistListReturn,
  consumeListReturn,
} from "../../utils/listNavigation";

const getDishImageUrl = (image) => {
  if (!image || typeof image !== "string") return Img;
  if (image.startsWith("http")) return image;
  const base = process.env.REACT_APP_IMAGE_URL || "";
  if (!base) return Img;
  return `${base.replace(/\/$/, "")}/${image.replace(/^\//, "")}`;
};

const normalizeExtractedStrings = (items) => {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => (typeof item === "string" ? item : item?.name || item?.title))
    .filter(Boolean);
};

const normalizeExtractedPrices = (items) => {
  if (!Array.isArray(items)) return [];
  return items.map((item) => ({
    extracted_variation: item.extracted_variation || item.variation?.name || item.variation || "N/A",
    price: Number(item.price) || 0,
  }));
};

const formatInternalList = (items, key = "name") => {
  if (!Array.isArray(items) || items.length === 0) return "No";
  return items.map((item) => item?.[key] || item?.dish_name || "N/A").join(", ");
};

const formatInternalAddons = (items) => {
  if (!Array.isArray(items) || items.length === 0) return "No";
  return items.map((item) => `${item.dish_name || item.name || "N/A"} (£${item.price ?? "N/A"})`).join(", ");
};

const formatInternalVariations = (items) => {
  if (!Array.isArray(items) || items.length === 0) return "No";
  return items
    .map((item) => `${item.variation?.name || "N/A"} (£${item.price ?? "N/A"})`)
    .join(", ");
};

const getPriceKey = (item) => `${item.extracted_variation}-${item.price}`;

const FIELD_ROWS = [
  { label: "Ingredients", field: "extracted_ingredient", internalKey: "ingredient" },
  { label: "Add-ons", field: "extracted_addon", internalKey: "dish_addon", internalType: "addon" },
  { label: "Cuisine", field: "extracted_cuisine", internalKey: "cuisine" },
  { label: "Allergies", field: "extracted_allergy", internalKey: "allergy" },
  { label: "Category", field: "extracted_dish_category", internalKey: "dish_category" },
  { label: "Meal Type", field: "extracted_meal_type", internalKey: "meal_type", internalNameKey: "title" },
  { label: "Variation", field: "extracted_dish_price", internalKey: "dish_price", internalType: "variation" },
];

const COLUMNS_PER_ROW = 3;

const getExtractedItems = (menuItem, row) => {
  if (row.field === "extracted_dish_price") {
    return normalizeExtractedPrices(menuItem?.extracted_dish_price);
  }
  return normalizeExtractedStrings(menuItem?.[row.field]);
};

const getInternalValue = (menuItem, row) => {
  const items = menuItem?.[row.internalKey];

  if (row.internalType === "addon") return formatInternalAddons(items);
  if (row.internalType === "variation") return formatInternalVariations(items);
  return formatInternalList(items, row.internalNameKey || "name");
};

const getInitialSelected = (menuItem) => {
  const selected = {};

  FIELD_ROWS.forEach((row) => {
    const items = getExtractedItems(menuItem, row);
    if (items.length === 0) return;

    if (row.field === "extracted_dish_price") {
      selected[row.field] = items;
    } else {
      selected[row.field] = items;
    }
  });

  return selected;
};

const LabelValueTable = ({ cells, columnsPerRow = COLUMNS_PER_ROW }) => {
  const rows = [];
  for (let i = 0; i < cells.length; i += columnsPerRow) {
    rows.push(cells.slice(i, i + columnsPerRow));
  }

  return (
    <div className="dish-approval-info-table w-100">
      <table className="w-100 text-center mb-0">
        <tbody>
          {rows.map((rowCells, rowIndex) => (
            <tr key={rowIndex}>
              {rowCells.map((cell, cellIndex) => (
                <td
                  key={cell.label}
                  colSpan={
                    cellIndex === rowCells.length - 1
                      ? columnsPerRow - rowCells.length + 1
                      : 1
                  }
                  className="px-3 py-3 align-top"
                >
                  <div className="dish-approval-info-table__label">{cell.label}</div>
                  <div className="mt-1">{cell.value}</div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

function ViewDishApproval() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [menuItem, setMenuItem] = useState(null);
  const [selectedExtracted, setSelectedExtracted] = useState({});

  const getDishReviewDetails = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await handleApiCall("get", `${endpoints.menuDishAdminReviewDetail}/${id}`);
      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        const data = response?.data?.data;
        setMenuItem(data);
        setSelectedExtracted(getInitialSelected(data));
      } else {
        toast.error(response?.data?.message || "Failed to fetch dish review details");
      }
    } catch (error) {
      console.error("Error fetching dish review details:", error);
      toast.error("Failed to fetch dish review details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (location.state?.from || location.state?.listState) {
      persistListReturn(
        LIST_RETURN_KEYS.DISH_APPROVAL.from,
        LIST_RETURN_KEYS.DISH_APPROVAL.state,
        location.state?.from,
        location.state?.listState
      );
    }
  }, [location.state]);

  const handleBackToList = () => {
    const { from, listState } = consumeListReturn(
      LIST_RETURN_KEYS.DISH_APPROVAL.from,
      LIST_RETURN_KEYS.DISH_APPROVAL.state,
      location.state
    );
    navigate(from || "/dish-approval", {
      state: listState ? { listState } : undefined,
    });
  };

  useEffect(() => {
    getDishReviewDetails();
  }, [getDishReviewDetails]);

  const extractedRows = useMemo(() => {
    if (!menuItem) return [];
    return FIELD_ROWS.filter((row) => getExtractedItems(menuItem, row).length > 0);
  }, [menuItem]);

  const toggleStringSelection = (field, value) => {
    setSelectedExtracted((prev) => {
      const current = prev[field] || [];
      return {
        ...prev,
        [field]: current.includes(value) ? current.filter((item) => item !== value) : [...current, value],
      };
    });
  };

  const togglePriceSelection = (priceItem) => {
    const key = getPriceKey(priceItem);
    setSelectedExtracted((prev) => {
      const current = prev.extracted_dish_price || [];
      const exists = current.some((item) => getPriceKey(item) === key);
      return {
        ...prev,
        extracted_dish_price: exists
          ? current.filter((item) => getPriceKey(item) !== key)
          : [...current, priceItem],
      };
    });
  };

  const isPriceSelected = (priceItem) =>
    (selectedExtracted.extracted_dish_price || []).some((item) => getPriceKey(item) === getPriceKey(priceItem));

  const handleApprove = async () => {
    if (!menuItem?._id) {
      toast.error("Review ID not found");
      return;
    }

    setApproving(true);
    try {
      const payload = { admin_review_id: menuItem._id };

      Object.entries(selectedExtracted).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          payload[key] = value;
        }
      });

      const response = await handleApiCall("post", endpoints.approveMenuDishAdminReview, payload);

      if (response?.data?.code === UI_CONSTANTS.API_CODES.SUCCESS) {
        toast.success(response?.data?.message || "Dish approved successfully");
        handleBackToList();
      }
    } catch (error) {
      console.error("Error approving dish review:", error);
    } finally {
      setApproving(false);
    }
  };

  const renderExtractedValue = (row) => {
    const extractedItems = getExtractedItems(menuItem, row);

    if (row.field === "extracted_dish_price") {
      return extractedItems.map((item) => (
        <label key={getPriceKey(item)} className="d-flex align-items-center justify-content-center mb-1">
          <input
            type="checkbox"
            className="mr-2"
            checked={isPriceSelected(item)}
            onChange={() => togglePriceSelection(item)}
          />
          <span>
            {item.extracted_variation} (£{item.price})
          </span>
        </label>
      ));
    }

    return extractedItems.map((item) => (
      <label key={item} className="d-flex align-items-center justify-content-center mb-1">
        <input
          type="checkbox"
          className="mr-2"
          checked={(selectedExtracted[row.field] || []).includes(item)}
          onChange={() => toggleStringSelection(row.field, item)}
        />
        <span>{item}</span>
      </label>
    ));
  };

  const dishImageSrc = menuItem ? getDishImageUrl(menuItem.image) : Img;
  const restaurant = menuItem?.restaurant || null;
  const isPending = String(menuItem?.extracted_dish_add_status || "PENDING").toUpperCase() === "PENDING";

  const internalCells = menuItem
    ? FIELD_ROWS.map((row) => ({
        label: row.label,
        value: getInternalValue(menuItem, row),
      }))
    : [];

  const extractedCells = menuItem
    ? extractedRows.map((row) => ({
        label: row.label,
        value: renderExtractedValue(row),
      }))
    : [];

  if (loading) {
    return (
      <div className="container-scroller">
        <Header />
        <div className="container-fluid page-body-wrapper">
          <Sidebar />
          <div className="main-panel">
            <div className="content-wrapper">
              <div className="heading mb-3 d-flex align-items-center">
                <h3 className="text-default weight-400 my-0">Dish Review Details</h3>
              </div>
              <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-scroller">
      <Header />
      <div className="container-fluid page-body-wrapper">
        <Sidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="heading mb-3 d-flex align-items-center">
              <h3 className="text-default weight-400 my-0">Dish Review Details</h3>
            </div>
            <div className="mt-3 d-flex align-items-center flex-wrap">
              <button type="button" className="btn btn-default rounded-lg" onClick={handleBackToList}>
                Back
              </button>
              {menuItem && isPending && extractedCells.length > 0 && (
                <button
                  type="button"
                  className="btn btn-success rounded-lg ml-2"
                  disabled={approving}
                  onClick={handleApprove}
                >
                  {approving ? "Approving..." : "Approve"}
                </button>
              )}
            </div>
            {menuItem ? (
              <>
                <div className="menu-item-detail__layout mt-4">
                  <div className="menu-item-detail__image">
                    <img
                      src={dishImageSrc}
                      alt={menuItem.dish_name || "Dish"}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = Img;
                      }}
                    />
                  </div>
                  <div className="menu-item-detail__content">
                    <h5 className="mb-2">{menuItem.dish_name || "N/A"}</h5>
                    <div className="border-bottom small mb-3 pb-3">
                      {menuItem.description || "No description available"}
                    </div>
                    <div className="small">
                      <div className="mb-1">
                        <span className="font-weight-600">Food Preference:</span>{" "}
                        {menuItem.food_preference?.name || "N/A"}
                      </div>
                      <div>
                        <span className="font-weight-600">Preparation Time:</span>{" "}
                        {menuItem.preparation_time ? `${menuItem.preparation_time} min` : "N/A"}
                      </div>
                    </div>

                    {restaurant && (
                      <div className="mt-4">
                        <h4 className="mb-3">RESTAURANT DETAILS</h4>
                        <LabelValueTable
                          cells={[
                            { label: "Restaurant Name", value: restaurant.restaurant_name || "N/A" },
                            { label: "Email", value: restaurant.email || "N/A" },
                            {
                              label: "Phone",
                              value:
                                restaurant.country_code && restaurant.phone
                                  ? `+${restaurant.country_code} ${restaurant.phone}`
                                  : restaurant.phone || "N/A",
                            },
                          ]}
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="row mt-4 px-0 mx-0 dish-approval-compare-row">
                  <div className={extractedCells.length > 0 ? "col-lg-6 pr-lg-3" : "col-12"}>
                    <h4 className="mb-3">INTERNAL</h4>
                    <LabelValueTable
                      cells={internalCells}
                      columnsPerRow={extractedCells.length > 0 ? 2 : 3}
                    />
                  </div>

                  {extractedCells.length > 0 && (
                    <div className="col-lg-6 pl-lg-3 mt-4 mt-lg-0">
                      <h4 className="mb-3">EXTRACTED</h4>
                      <LabelValueTable cells={extractedCells} columnsPerRow={2} />
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <p>No dish review details available</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ViewDishApproval;
